import { useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  HandCoins,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Link, useNavigate } from '@tanstack/react-router';
import { cn, formatRelativeTime } from '@/lib/utils';
import { useFinanceMetrics } from '@/hooks/queries/useFinanceMetrics';
import { useFinanceHealth, useFinanceDeadlines } from '@/hooks/queries/useFinanceDeadlines';
import { useFinanceReports, useDownloadReportPackage } from '@/hooks/queries/useFinanceReports';
import { useFinanceSettings, useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useOnboardingStatus, isOnboardingIncomplete } from '@/hooks/queries/useFinanceOnboarding';
import { usePaymentsStatus } from '@/hooks/queries/useStripeConnectPayments';
import { useDashboardSummary } from '@/hooks/queries/useDashboardSummary';
import { useLastFiledCash } from '@/hooks/queries/useLastFiledCash';
import { useDashboardRoster } from '@/hooks/queries/useDashboardRoster';
import { useDashboardAttention } from '@/hooks/queries/useDashboardAttention';
import { useBankConnections, useSyncBankConnection } from '@/hooks/queries/useBankConnections';
import { useCancelOverLimitRecurring } from '@/hooks/queries/useCancelOverLimitRecurring';
import { useCampaignStore } from '@/stores';
import { usePortalUploadModel, type PortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { daysUntil, dueTimeSuffix } from '../utils';
import { FilingExposureNotes } from '../FilingExposureNotes';
import { DeadlineStatus, ReportStatus, BankConnectionStatus, jurisdictionFormLabel, duplicateDonorLeadLabel } from '@signalvote/shared';
import type { FilingDeadlineDto, ComplianceReportDto, DashboardSummaryDto, DashboardAttentionDto, FinanceHealthDto, LastFiledCashDto, DashboardRosterDto, AttentionContributionDto, DonorPositionAlarm, DonorPositionOverage, OnboardingStatusDto, PaymentsStatusDto } from '@signalvote/shared';
import { mapRead, match, ReadView, whenReady, type CurrentRead, type Read } from '@signalvote/data';
import { computePeriodWindow, type PeriodId } from './periodWindow';
import { triggerBrowserDownload } from '../reports/downloadHelpers';
import { statusBadge } from '../reports/reportUtils';
import { NeedsAmendmentAlerts } from '@/components/finance/shared';

// ── Helpers ─────────────────────────────────────────────────────────────────
function fmtMoneyBig(n: number): string {
  const abs = Math.abs(n);
  return `${n < 0 ? '-' : ''}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtMoneyShort(n: number): string {
  const abs = Math.abs(n);
  return `${n < 0 ? '-' : ''}$${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}
// These two intentionally do NOT slice internally — every dashboard callsite
// already passes a pre-sliced YYYY-MM-DD (e.g. `next.dueDate.slice(0, 10)`), so
// they stay local rather than folding into the close/ copies (which DO slice).
// `daysUntil` was byte-identical to the shared helper, so it's imported.
function fmtDateLong(s: string): string {
  const d = new Date(s + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtDateShort(s: string): string {
  const d = new Date(s + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
function urgencyTone(days: number): 'blue' | 'amber' | 'rose' {
  if (days <= 7) return 'rose';
  if (days <= 21) return 'amber';
  return 'blue';
}

// ── Hero (totals) ───────────────────────────────────────────────────────────
// Prop-driven (ADR-017): the parent resolves the period window, fetches the
// windowed summary + the frozen last-filed cash, and threads them in. No
// SEED_TOTALS, no live cumulative cash-on-hand, no synthetic raise-vs-spend
// chart. Exported for the prop-driven component test (direct import, not a
// barrel) — same pattern as ReportsRow.
export function DashHero({
  period,
  setPeriod,
  summary,
  lastFiledCash,
  customFrom,
  customTo,
  onCustomRange,
  stagedCount,
  owedReportName,
}: {
  period: PeriodId;
  setPeriod: (p: PeriodId) => void;
  summary: CurrentRead<DashboardSummaryDto>;
  lastFiledCash: CurrentRead<LastFiledCashDto | null>;
  customFrom: string;
  customTo: string;
  onCustomRange: (from: string, to: string) => void;
  /** §15 — staged (ready) row count from the server-computed
   *  `health.owedReportReadiness` pips (ADR-026 PR-A), the same source the
   *  "Next report due" card reads. Undefined while health loads. */
  stagedCount?: number;
  /** §15 — the OWED report's name (`health.reportToActOn`). The empty-period hint
   *  points here, NOT at the empty next report (the prior lie). Null when caught
   *  up (no unfiled report with owed activity) → the hint suppresses. */
  owedReportName?: string | null;
}) {
  const [customOpen, setCustomOpen] = useState(false);

  const periods: { id: Exclude<PeriodId, 'custom'>; label: string }[] = [
    { id: 'current_period', label: 'This filing period' },
    { id: 'ytd', label: 'Year to date' },
    { id: 'cycle', label: 'Campaign to date' },
  ];

  const periodLabel =
    periods.find((p) => p.id === period)?.label ?? 'Custom range';

  // §15 — an empty current filing period ($0 / 0 transactions) hides that the
  // treasurer's in-progress work belongs to an EARLIER, still-unfiled report. The
  // prior copy pointed at the empty NEXT report ("your N staged items are in the
  // {nextReport} below") — a lie: those items never belong to the empty next
  // period. Point at the OWED report (`health.reportToActOn`) instead. Only on the
  // current-period tab — YTD / cycle / custom show real cumulative windows. Shown
  // only when the period is genuinely empty AND an owed report exists.
  const periodEmpty =
    period === 'current_period' &&
    summary.status === 'ready' &&
    summary.data.contributionCount === 0 &&
    summary.data.expenseCount === 0 &&
    summary.data.inKindCount === 0;
  const showStagedHint = periodEmpty && (stagedCount ?? 0) > 0 && !!owedReportName;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-3.5 shadow-sm">
      <div className="flex justify-between items-start gap-5 mb-4">
        <div>
          <div className="text-[11px] font-bold text-slate-400 tracking-[.08em] font-mono">PERIOD TOTALS</div>
          <div className="text-lg font-extrabold text-slate-900 tracking-[-.01em] mt-1">{periodLabel}</div>
          {showStagedHint && (
            <p
              data-testid="period-empty-staged-hint"
              className="text-[12.5px] text-slate-500 mt-1.5 max-w-xl"
            >
              {stagedCount}{' '}
              {stagedCount === 1 ? 'transaction is' : 'transactions are'} waiting in an
              earlier report — {owedReportName}. File that first.
            </p>
          )}
        </div>
        <div className="relative">
          <div className="inline-flex gap-0.5 bg-slate-100 rounded-lg p-[3px]">
            {periods.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setPeriod(p.id);
                  setCustomOpen(false);
                }}
                className={cn(
                  'px-3 py-1.5 rounded-md text-[12.5px] font-semibold transition-colors',
                  period === p.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
                )}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                setPeriod('custom');
                setCustomOpen((o) => !o);
              }}
              className={cn(
                'px-3 py-1.5 rounded-md text-[12.5px] font-semibold inline-flex items-center gap-1.5 border-l border-slate-200 ml-0.5 pl-2.5 transition-colors',
                period === 'custom' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
              )}
            >
              <Calendar className="h-[13px] w-[13px]" />
              {period === 'custom' ? 'Custom' : 'Custom range'}
              <ChevronDown className="h-[13px] w-[13px]" />
            </button>
          </div>
          {customOpen && (
            <div className="absolute top-[calc(100%+6px)] right-0 bg-white border border-slate-200 rounded-xl shadow-lg p-3.5 w-[300px] z-20">
              <div className="text-[11px] font-bold text-slate-500 tracking-[.06em] uppercase mb-2.5">Custom date range</div>
              <div className="grid grid-cols-2 gap-2.5">
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold text-slate-500">From</span>
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo}
                    onChange={(e) => onCustomRange(e.target.value, customTo)}
                    className="px-2 py-1.5 border border-slate-200 rounded-md text-[12.5px] font-mono outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                  />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-[11px] font-semibold text-slate-500">To</span>
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom}
                    onChange={(e) => onCustomRange(customFrom, e.target.value)}
                    className="px-2 py-1.5 border border-slate-200 rounded-md text-[12.5px] font-mono outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10"
                  />
                </label>
              </div>
              <div className="flex justify-end gap-1.5 mt-3.5 pt-3 border-t border-slate-200">
                <button
                  type="button"
                  className="px-2.5 py-1 text-xs font-semibold text-slate-700 rounded-md hover:bg-slate-100"
                  onClick={() => setCustomOpen(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="px-2.5 py-1 text-xs font-semibold bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={() => setCustomOpen(false)}
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Three flow stats under the period switcher (period-scoped flows). */}
      <div className="grid grid-cols-1 sm:grid-cols-3 border-y border-slate-200">
        <StatCell
          label="Contributions"
          read={summary}
          value={(s) => fmtMoneyBig(s.contributions)}
          meta={(s) => `${s.contributionCount} transactions`}
          valueColor="text-emerald-700"
        />
        <StatCell
          label="Expenses"
          read={summary}
          value={(s) => fmtMoneyBig(s.expenses)}
          meta={(s) => `${s.expenseCount} transactions`}
          valueColor="text-rose-700"
        />
        <StatCell
          label="In-kind"
          read={summary}
          value={(s) => fmtMoneyBig(s.inKind)}
          meta={(s) => `${s.inKindCount} contributions`}
          valueColor="text-blue-700"
        />
      </div>

      {/* Cash on hand — a STOCK, not a per-period flow, so it sits OUTSIDE the
          period switcher group. Only ever the frozen last-filed figure (ADR-017);
          "No filing yet" when none filed, never $0. */}
      <CashOnHandCell read={lastFiledCash} />
    </div>
  );
}

// Skeleton while the read is in flight, never a fabricated 0 (ADR-017).
function CellSkeleton({ width }: { width: string }) {
  return <div className={cn('h-[26px] rounded bg-slate-100 animate-pulse', width)} />;
}

function CellNote({ children }: { children: ReactNode }) {
  return <div className="text-[13px] text-slate-400 font-medium">{children}</div>;
}

const COULD_NOT_LOAD = 'Couldn’t load';
const WAITING_FOR_CONNECTION = 'Waiting for a connection';

function StatCell<T>({
  label,
  read,
  value,
  meta,
  valueColor,
}: {
  label: string;
  read: CurrentRead<T>;
  value: (data: T) => string;
  meta: (data: T) => string;
  valueColor: string;
}) {
  const skeleton = () => (
    <>
      <CellSkeleton width="w-32" />
      <div className="h-[14px] w-20 rounded bg-slate-100 animate-pulse mt-2.5" />
    </>
  );
  return (
    <div className="px-5 py-4.5 border-r border-slate-200 last:border-r-0 first:pl-0">
      <div className="flex justify-between items-center mb-2.5 gap-2.5">
        <div className="text-[11.5px] font-bold text-slate-500 tracking-[.04em] uppercase">{label}</div>
      </div>
      <ReadView
        read={read}
        idle={skeleton}
        loading={skeleton}
        failed={() => <CellNote>{COULD_NOT_LOAD}</CellNote>}
        offline={() => <CellNote>{WAITING_FOR_CONNECTION}</CellNote>}
        ready={({ data }) => (
          <>
            <div className={cn('text-[26px] font-extrabold tracking-[-.02em] leading-none font-mono', valueColor)}>
              {value(data)}
            </div>
            <div className="text-[11.5px] text-slate-500 mt-1.5 font-medium font-mono">{meta(data)}</div>
          </>
        )}
      />
    </div>
  );
}

// The dashboard's only cash-on-hand figure — the FROZEN last-filed report value
// (ADR-017). Never a live cumulative balance. Renders "No filing yet" (never $0)
// only when the read answered that no report has been filed.
function CashOnHandCell({ read }: { read: CurrentRead<LastFiledCashDto | null> }) {
  const skeleton = () => <CellSkeleton width="w-40" />;
  return (
    <div className="pt-4 mt-0.5">
      <div className="text-[11.5px] font-bold text-slate-500 tracking-[.04em] uppercase mb-2">
        Cash on hand at last filing
      </div>
      <ReadView
        read={read}
        idle={skeleton}
        loading={skeleton}
        failed={() => <CellNote>{COULD_NOT_LOAD}</CellNote>}
        offline={() => <CellNote>{WAITING_FOR_CONNECTION}</CellNote>}
        ready={({ data }) =>
          data ? (
            <>
              <div className="text-[26px] font-extrabold tracking-[-.02em] leading-none font-mono text-slate-900">
                {fmtMoneyBig(data.cashOnHand)}
              </div>
              <div className="text-[11.5px] text-slate-500 mt-1.5 font-medium font-mono">
                as of {fmtDateLong(data.filedDate)} · {data.reportName}
              </div>
            </>
          ) : (
            <div className="text-[20px] font-bold text-slate-400 tracking-[-.01em] leading-none">No filing yet</div>
          )
        }
      />
    </div>
  );
}

// ── Reports row ─────────────────────────────────────────────────────────────
// Prop-driven (ADR-017): every number/label is REAL. The parent threads the
// resolved next-deadline + last-filed report plus the campaign-wide Staged /
// Flagged counts (from useFinanceHealth) and the View / Download callbacks.
// There are no fabricated fallbacks: no seed "Q2 2026" card, no 84/7/~2400
// stats, no hardcoded "ACCEPTED" pill, no "Filed by Treasurer" literal, no dead
// buttons. Null next/last render honest empty states (mirror DashHero's
// "No filing yet"). Exported for the prop-driven component test (direct import,
// not a barrel) — same pattern as DashHero / AccountsPanel.
export function ReportsRow({
  next,
  secondaryDeadline,
  caughtUp,
  last,
  amendmentPending,
  rulesetCode,
  portalUpload,
  stagedCount,
  flaggedCount,
  pendingExportReport,
  onJumpToFilings,
  onViewReport,
  onDownloadPackage,
  onGoConfirmExport,
}: {
  /** The LEAD deadline the card names — the owed report (`health.reportToActOn`)
   *  when present, else the next chronological deadline. */
  next: FilingDeadlineDto | null;
  /** The next CHRONOLOGICAL deadline, shown as secondary context when the lead is
   *  an EARLIER owed report (reportToActOnIsNextDeadline === false). Null/omitted
   *  when the lead already IS the next deadline. */
  secondaryDeadline?: FilingDeadlineDto | null;
  /** True when caught up (no owed report) but an upcoming deadline exists —
   *  drives the "all caught up" state over the next upcoming deadline. */
  caughtUp?: boolean;
  last: ComplianceReportDto | null;
  /** True when any report is awaiting an amendment. The `last` query asks for
   *  `status === Filed` only, so the flip empties it — and the empty state must not then
   *  assert "No filing yet" inches from a card naming a report that WAS filed. */
  amendmentPending?: boolean;
  /** Drives the `jurisdictionFormLabel` lookup at the render boundary so the
   *  next-deadline subtitle reads as state-filer copy (e.g., AL `Annual Report`
   *  → `Campaign Finance Disclosure Report (Annual)`). Null when settings
   *  haven't loaded yet — the helper falls back to the raw `scheduleName`. */
  rulesetCode: string | null;
  /** Filing-model boundary (§30/§32). When the resolved workflow is PortalOnly,
   *  the cards add "you upload to the portal / it doesn't file for you" copy.
   *  Undefined/non-portal ⇒ no portal captions (federal in-system e-file). */
  portalUpload?: PortalUploadModel | null;
  /** Campaign-wide Confirmed (staged) row count from useFinanceHealth.
   *  `undefined` while health is still loading → muted "—", never a
   *  fabricated 0 (ADR-017). A loaded 0 is honest and renders as 0. */
  stagedCount: number | undefined;
  /** Red/Yellow needs-attention flag count from useFinanceHealth. Same
   *  loading semantics as stagedCount. */
  flaggedCount: number | undefined;
  /** When non-null, replaces the "Next report due" card with the pending-export
   *  confirmation card until the treasurer resolves it. */
  pendingExportReport?: { reportId: string; reportName: string; rowCount: number; generatedAt: string } | null;
  onJumpToFilings: () => void;
  /** Navigate to the filed report's detail page. */
  onViewReport: (reportId: string) => void;
  /** Download the filed report's submission package (ZIP). */
  onDownloadPackage: (reportId: string) => void;
  /** Navigate to the pending-export report's detail page to confirm or discard. */
  onGoConfirmExport?: (reportId: string) => void;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-3.5 mb-3.5">
      {pendingExportReport ? (
        <PendingExportCard
          pendingExportReport={pendingExportReport}
          onGoConfirm={onGoConfirmExport ?? (() => {})}
        />
      ) : (
        <NextReportCard
          next={next}
          secondaryDeadline={secondaryDeadline}
          caughtUp={caughtUp}
          rulesetCode={rulesetCode}
          portalUpload={portalUpload}
          stagedCount={stagedCount}
          flaggedCount={flaggedCount}
          onJumpToFilings={onJumpToFilings}
        />
      )}
      <LastReportCard
        last={last}
        amendmentPending={amendmentPending}
        onViewReport={onViewReport}
        onDownloadPackage={onDownloadPackage}
      />
    </div>
  );
}

/**
 * Replaces the "Next report due" card when an export is awaiting upload confirmation.
 * The treasurer must resolve this before it snaps back to the regular deadline card.
 * Exported for use in CloseMasthead.
 */
export function PendingExportCard({
  pendingExportReport,
  onGoConfirm,
}: {
  pendingExportReport: { reportId: string; reportName: string; rowCount: number; generatedAt: string };
  onGoConfirm: (reportId: string) => void;
}) {
  const rowCount = pendingExportReport.rowCount;
  const rowLabel = rowCount === 1 ? 'transaction' : 'transactions';

  return (
    <div
      className="bg-white border-2 border-amber-400 rounded-2xl overflow-hidden flex flex-col shadow-sm animate-amber-border-pulse"
      data-testid="pending-export-card"
    >
      <div className="p-5 flex flex-col gap-3">
      <div className="flex justify-between items-center gap-3">
        <div className="text-[10.5px] font-bold text-slate-400 tracking-[.08em] font-mono">
          UPLOAD CONFIRMATION NEEDED
        </div>
        <span className="inline-flex items-center text-[10.5px] font-extrabold px-2.5 py-1 rounded tracking-[.04em] font-mono bg-amber-100 text-amber-700">
          ACTION NEEDED
        </span>
      </div>
      <div>
        <div className="text-lg font-extrabold text-slate-900 tracking-[-.01em]">
          {pendingExportReport.reportName}
        </div>
        <div className="text-xs text-slate-500 font-mono mt-0.5">
          {rowCount} {rowLabel} · exported{' '}
          {fmtDateLong(pendingExportReport.generatedAt.slice(0, 10))}
        </div>
      </div>
      <p className="text-[12.5px] text-slate-600">
        You downloaded an export to upload to the portal. Come back here after uploading to
        confirm whether it was accepted.
      </p>
      <button
        onClick={() => onGoConfirm(pendingExportReport.reportId)}
        className="mt-auto self-start rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 transition-colors"
        data-testid="pending-export-go-confirm"
      >
        Confirm or discard →
      </button>
    </div>
    </div>
  );
}

// Muted "—" placeholder (loading) vs the real count (incl. an honest 0).
function StatNumber({ value, color }: { value: number | undefined; color: string }) {
  if (value === undefined) {
    return <div className="text-xl font-extrabold text-slate-300 tracking-[-.02em] leading-none font-mono">—</div>;
  }
  return <div className={cn('text-xl font-extrabold tracking-[-.02em] leading-none font-mono', color)}>{value}</div>;
}

function NextReportCard({
  next,
  secondaryDeadline,
  caughtUp,
  rulesetCode,
  portalUpload,
  stagedCount,
  flaggedCount,
  onJumpToFilings,
}: {
  next: FilingDeadlineDto | null;
  secondaryDeadline?: FilingDeadlineDto | null;
  caughtUp?: boolean;
  rulesetCode: string | null;
  portalUpload?: PortalUploadModel | null;
  stagedCount: number | undefined;
  flaggedCount: number | undefined;
  onJumpToFilings: () => void;
}) {
  // Honest empty state — no fabricated deadline (ADR-017).
  if (!next) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3 shadow-sm border-t-[3px] border-t-slate-200">
        <div className="text-[10.5px] font-bold text-slate-400 tracking-[.08em] font-mono">NEXT REPORT DUE</div>
        <div className="flex-1 flex flex-col justify-center py-6">
          <div className="text-[20px] font-bold text-slate-400 tracking-[-.01em] leading-none">No upcoming report</div>
          <div className="text-[12.5px] text-slate-400 mt-1.5">Nothing due right now.</div>
        </div>
      </div>
    );
  }

  const days = daysUntil(next.dueDate);
  const tone = urgencyTone(days);
  // Translate the raw `scheduleName` at the render boundary into state-filer
  // copy so the engineer-facing schedule name never leaks into treasurer copy.
  const nextForm = next.scheduleName ? jurisdictionFormLabel(rulesetCode ?? '', next.scheduleName) : null;
  // A RemindOnly lead deadline (the AL annual) is filed directly in the portal —
  // the app builds no file for it, so the card frames it as "file in the portal",
  // never a Prepare/Export CTA.
  const remindOnly = next.preparationMode === 'RemindOnly';
  const remindOnlyPortal =
    portalUpload?.stateDisplayName
      ? `${portalUpload.stateDisplayName} ${portalUpload.portalName}`
      : 'state portal';

  // Progress bar only renders when both real period bounds exist — never
  // fabricate dates to fill it (ADR-017). "Today" is campaign-timezone, never
  // browser-local Date.now().
  const hasPeriod = !!next.periodStart && !!next.periodEnd;
  let progress: { start: string; end: string; elapsed: number; totalDays: number; elapsedPct: number } | null = null;
  if (hasPeriod) {
    const startStr = next.periodStart!;
    const endStr = next.periodEnd!;
    const start = new Date(startStr.slice(0, 10) + 'T12:00:00');
    const end = new Date(endStr.slice(0, 10) + 'T12:00:00');
    const today = new Date(getCampaignTodayString() + 'T12:00:00');
    const totalDays = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000));
    const elapsed = Math.max(0, Math.min(totalDays, Math.round((today.getTime() - start.getTime()) / 86400000)));
    progress = { start: startStr, end: endStr, elapsed, totalDays, elapsedPct: Math.round((elapsed / totalDays) * 100) };
  }

  const toneBorder = {
    blue: 'border-t-blue-600',
    amber: 'border-t-amber-500',
    rose: 'border-t-rose-500',
  }[tone];

  const tonePill = {
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700',
  }[tone];

  return (
    <div className={cn('bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3 shadow-sm border-t-[3px]', toneBorder)}>
      <div className="flex justify-between items-center gap-3">
        <div className="text-[10.5px] font-bold text-slate-400 tracking-[.08em] font-mono">NEXT REPORT DUE</div>
        <span className={cn('inline-flex items-center gap-1 text-[10.5px] font-extrabold px-2.5 py-1 rounded tracking-[.04em] font-mono', tonePill)}>
          {days > 0 ? `${days} DAYS` : days === 0 ? 'DUE TODAY' : `${Math.abs(days)} DAYS LATE`}
        </span>
      </div>
      {/* Caught up: no report owes the treasurer's activity, so this card is the
          next upcoming deadline for context, prefaced by the all-clear state. */}
      {caughtUp && (
        <p
          data-testid="next-report-caught-up"
          className="text-[12.5px] font-semibold text-emerald-700"
        >
          You&apos;re all caught up — nothing is waiting to be filed. Next up:
        </p>
      )}
      <div>
        <div className="text-lg font-extrabold text-slate-900 tracking-[-.01em]">{next.name}</div>
        <div className="text-xs text-slate-500 font-mono">
          {nextForm ? `${nextForm} · ` : ''}due {fmtDateLong(next.dueDate.slice(0, 10))}
          {dueTimeSuffix(next.dueTime)}
        </div>
        <FilingExposureNotes deadline={next} testIdPrefix="next-report" />
        {/* The next CHRONOLOGICAL deadline, demoted to secondary context when the
            lead is an earlier owed report. */}
        {secondaryDeadline && (
          <p
            data-testid="next-report-secondary-deadline"
            className="text-[11.5px] text-slate-500 font-mono mt-1"
          >
            Next deadline: {secondaryDeadline.name} · due{' '}
            {fmtDateLong(secondaryDeadline.dueDate.slice(0, 10))}
            {dueTimeSuffix(secondaryDeadline.dueTime)}
          </p>
        )}
      </div>

      {progress && (
        <div>
          <div className="h-1.5 bg-slate-100 rounded-sm overflow-hidden">
            <div className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-sm" style={{ width: `${progress.elapsedPct}%` }} />
          </div>
          <div className="flex justify-between text-[10.5px] text-slate-500 mt-1.5 font-semibold font-mono">
            <span>{fmtDateLong(progress.start.slice(0, 10))}</span>
            <span className="text-blue-700">
              Today · day {progress.elapsed} of {progress.totalDays}
            </span>
            <span>{fmtDateLong(progress.end.slice(0, 10))}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3.5 py-3 border-y border-slate-200">
        <div>
          <StatNumber value={stagedCount} color="text-slate-900" />
          <div className="text-[10.5px] text-slate-500 uppercase tracking-[.05em] font-semibold mt-1">Staged</div>
        </div>
        <div>
          <StatNumber value={flaggedCount} color="text-amber-600" />
          <div className="text-[10.5px] text-slate-500 uppercase tracking-[.05em] font-semibold mt-1">Flagged</div>
        </div>
      </div>

      {remindOnly ? (
        /* RemindOnly (the AL annual): the app builds no file for this report —
           it's filed directly in the portal. Frame it as "file in the portal",
           never a Prepare/Export CTA. Jurisdiction name from stateDisplayName. */
        <p
          data-testid="next-report-remind-only"
          className="text-[12px] text-slate-600 leading-snug mt-auto"
        >
          File this one directly in the {remindOnlyPortal} — we don&apos;t prepare a file
          for it.
        </p>
      ) : (
        <>
          <div className="flex gap-2 mt-auto">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700"
              onClick={onJumpToFilings}
            >
              Continue preparing <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function LastReportCard({
  last,
  amendmentPending,
  onViewReport,
  onDownloadPackage,
}: {
  last: ComplianceReportDto | null;
  amendmentPending?: boolean;
  onViewReport: (reportId: string) => void;
  onDownloadPackage: (reportId: string) => void;
}) {
  // Honest empty state — no fabricated last report (ADR-017). Mirrors
  // DashHero's null cash-on-hand copy/style.
  //
  // "No filing yet" is a claim about history, and the `status === Filed` query this card
  // reads cannot see a report that has flipped to NeedsAmendment. So when something IS
  // awaiting amendment, that claim is false — the committee filed, and the amendment card
  // above says so by name. Point at it instead of contradicting it.
  if (!last) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3 shadow-sm border-t-[3px] border-t-slate-200">
        <div className="text-[10.5px] font-bold text-slate-400 tracking-[.08em] font-mono">LAST REPORT FILED</div>
        {amendmentPending ? (
          <div data-testid="last-report-amendment-pending" className="flex-1 flex flex-col justify-center py-6">
            <div className="text-[20px] font-bold text-orange-700 tracking-[-.01em] leading-none">Needs amending</div>
            <div className="text-[12.5px] text-slate-500 mt-1.5">
              Your filing needs an amendment — see the notice above.
            </div>
          </div>
        ) : (
          <div data-testid="last-report-none-filed" className="flex-1 flex flex-col justify-center py-6">
            <div className="text-[20px] font-bold text-slate-400 tracking-[-.01em] leading-none">No filing yet</div>
            <div className="text-[12.5px] text-slate-400 mt-1.5">Your filed reports will show up here.</div>
          </div>
        )}
      </div>
    );
  }

  // Real status from the DTO. `statusBadge` is reused defensively (it covers the
  // full status range), but the dashboard's list query only ever returns Filed
  // reports — so in practice this renders "Filed". There is no "Accepted" status
  // and the receipt fields are null on the list DTO.
  const badge = statusBadge(last);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3 shadow-sm border-t-[3px] border-t-emerald-500">
      <div className="flex justify-between items-center gap-3">
        <div className="text-[10.5px] font-bold text-slate-400 tracking-[.08em] font-mono">LAST REPORT FILED</div>
        <span className={cn('inline-flex items-center gap-1 text-[10.5px] font-extrabold px-2.5 py-1 rounded tracking-[.04em] font-mono', badge.className)}>
          {badge.label}
        </span>
      </div>
      <div>
        <div className="text-lg font-extrabold text-slate-900 tracking-[-.01em]">{last.name}</div>
        {last.filedDate && (
          <div className="text-xs text-slate-500 font-mono">
            filed {fmtDateLong(last.filedDate.slice(0, 10))}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 py-3 border-y border-slate-200">
        <div className="flex justify-between items-baseline gap-3 text-[12.5px]">
          <span className="text-slate-500 font-medium">Period covered</span>
          <span className="text-slate-900 font-semibold text-right font-mono">
            {fmtDateLong(last.periodStart.slice(0, 10))} → {fmtDateLong(last.periodEnd.slice(0, 10))}
          </span>
        </div>
        <div className="flex justify-between items-baseline gap-3 text-[12.5px]">
          <span className="text-slate-500 font-medium">Amendments</span>
          <span className="text-slate-900 font-semibold text-right font-mono">{last.amendmentNumber}</span>
        </div>
      </div>

      <div className="flex gap-2 mt-auto">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100"
          onClick={() => onViewReport(last.id)}
        >
          <Eye className="h-3.5 w-3.5" /> View report
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100"
          onClick={() => onDownloadPackage(last.id)}
        >
          <Download className="h-3.5 w-3.5" /> Download package
        </button>
      </div>
    </div>
  );
}

// ── Accounts panel ──────────────────────────────────────────────────────────
// Prop-driven (ADR-017): the parent fetches the roster via useDashboardRoster
// and threads it in. Two provenance-only sections — Bank accounts and Sources
// you import from. NO money-at-rest figure anywhere: no balance, no per-source
// dollar total, no totals row. The Bank accounts section carries a real "Sync now"
// action (the free `/transactions/sync` cached pull — never a billable refresh),
// gated on a live connection existing (canSync); there is still no fake "Sync all"
// and no money figure. Exported for the prop-driven component test (direct import,
// not a barrel) — same pattern as DashHero / ReportsRow.

// "Last import" recency for a roster row. Sources older than this many days read
// as "stale"; null means never imported.
const STALE_IMPORT_DAYS = 30;

// Pure + testable: diff against CAMPAIGN-today (a YYYY-MM-DD threaded from
// getCampaignTodayString()), never browser-local Date.now() (ADR-017 timezone
// golden rule). Both anchors parse at T12:00:00 so the day count is DST-stable.
function importRecencyDays(lastImportAt: string | null, campaignToday: string): number | null {
  if (!lastImportAt) return null;
  const imported = new Date(lastImportAt + (lastImportAt.includes('T') ? '' : 'T12:00:00'));
  const today = new Date(campaignToday + 'T12:00:00');
  return Math.round((today.getTime() - imported.getTime()) / 86400000);
}

function fmtLastImport(lastImportAt: string | null): string {
  if (!lastImportAt) return '—';
  return fmtDateLong(lastImportAt.includes('T') ? lastImportAt.slice(0, 10) : lastImportAt);
}

export function AccountsPanel({
  roster,
  campaignToday,
  onJumpToSources,
  canSync = false,
  onSyncBanks,
  syncing = false,
}: {
  roster: CurrentRead<DashboardRosterDto>;
  /** Campaign-today (YYYY-MM-DD) from getCampaignTodayString() — drives the
   *  Current/Stale recency badge off campaign timezone, not the wall clock. */
  campaignToday: string;
  onJumpToSources: () => void;
  /** True when ≥1 live (Active) Plaid connection exists — gates the Sync now action.
   *  A campaign with only manually-imported accounts has nothing to sync. */
  canSync?: boolean;
  /** Runs the free `/transactions/sync` cached pull across the live connections. */
  onSyncBanks?: () => void;
  /** A sync is in flight — disables the button. */
  syncing?: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-3.5 shadow-sm">
      <div className="flex justify-between items-start gap-4 mb-3.5 flex-wrap">
        <div>
          <div className="text-base font-extrabold text-slate-900 tracking-[-.01em]">Accounts &amp; sources</div>
        </div>
        <button
          type="button"
          onClick={onJumpToSources}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100"
        >
          Manage <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>

      <ReadView
        read={roster}
        failed={() => (
          <div className="border-t border-slate-200 pt-4 text-[13px] text-slate-500">
            We couldn’t load your accounts &amp; sources right now.
          </div>
        )}
        offline={() => (
          <div className="border-t border-slate-200 pt-4 text-[13px] text-slate-500">
            You’re offline. Your accounts &amp; sources will load when you reconnect.
          </div>
        )}
        idle={rosterSkeleton}
        loading={rosterSkeleton}
        ready={({ data }) => (
        <div className="space-y-5">
          {/* ── Bank accounts ── */}
          <section>
            <div className="flex items-center justify-between gap-3 border-b border-slate-200 pb-1.5 mb-1">
              <div className="text-[10.5px] font-bold text-slate-500 tracking-[.05em] uppercase">
                Bank accounts
              </div>
              {/* Sync now — the free `/transactions/sync` cached pull, so a treasurer about
                  to file can grab the latest before the daily poll runs. Only shown when a
                  live connection exists (nothing to sync otherwise). NOT a billable refresh. */}
              {canSync && (
                <button
                  type="button"
                  onClick={onSyncBanks}
                  disabled={syncing}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                >
                  {syncing ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  Sync now
                </button>
              )}
            </div>
            {canSync && (
              <p className="text-[11px] text-slate-500 mb-1.5">
                Pulls the latest transactions your bank has already shared — no live fetch,
                always free.
              </p>
            )}
            {data.bankAccounts.length === 0 ? (
              <div className="py-3 text-[13px] text-slate-500">
                No bank accounts linked yet — import a bank statement to add one.
              </div>
            ) : (
              data.bankAccounts.map((a) => (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-3.5 px-1 py-2.5 text-[13px] text-slate-700 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-bold text-slate-900 truncate">{a.displayName}</div>
                    {/* Prefer the live refresh recency for a Plaid-connected account —
                        it advances on every sync, even an empty poll — and fall back to
                        the import date for a manually-imported account with no sync. */}
                    <div className="text-[11.5px] text-slate-500">
                      {a.lastRefreshedAt
                        ? `Refreshed ${formatRelativeTime(a.lastRefreshedAt)}`
                        : `Last import ${fmtLastImport(a.lastImportAt)}`}
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 font-medium whitespace-nowrap">
                    {a.transactionCount} {a.transactionCount === 1 ? 'transaction' : 'transactions'}
                  </div>
                </div>
              ))
            )}
          </section>

          {/* ── Sources you import from ── */}
          <section>
            <div className="text-[10.5px] font-bold text-slate-500 tracking-[.05em] uppercase border-b border-slate-200 pb-1.5 mb-1">
              Sources you import from
            </div>
            {data.sources.length === 0 ? (
              <div className="py-3 text-[13px] text-slate-500">No import sources yet.</div>
            ) : (
              data.sources.map((s) => {
                const days = importRecencyDays(s.lastImportAt, campaignToday);
                return (
                  <div
                    key={s.sourceKey}
                    className="flex items-center justify-between gap-3.5 px-1 py-2.5 text-[13px] text-slate-700 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-bold text-slate-900 truncate">{s.displayName}</div>
                      <div className="text-[11.5px] text-slate-500">Last import {fmtLastImport(s.lastImportAt)}</div>
                    </div>
                    {days === null ? (
                      <span className="text-[11px] text-slate-400 font-semibold whitespace-nowrap">Never imported</span>
                    ) : days > STALE_IMPORT_DAYS ? (
                      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap">
                        <AlertTriangle className="h-3 w-3" /> Stale
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-[11px] font-bold whitespace-nowrap">
                        <Check className="h-3 w-3" /> Current
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </section>
        </div>
        )}
      />
    </div>
  );
}

function rosterSkeleton() {
  return (
    <div className="border-t border-slate-200 pt-4 space-y-2.5">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-9 rounded-md bg-slate-100 animate-pulse" />
      ))}
    </div>
  );
}

const NO_ROWS: never[] = [];

type AttentionView =
  | { state: 'ready'; data: DashboardAttentionDto }
  | { state: 'loading' | 'error'; data: null };

// ── Attention panel ─────────────────────────────────────────────────────────
// Prop-driven (ADR-017) — the parent fetches via useDashboardAttention and threads
// the two real lists + loading/error in (like DashHero / AccountsPanel). v1 = two
// categories: over-limit + incomplete donor info. Each row is a real <Link> into
// the Close ledger focused on its contribution (the TriageLedger deep-link); there
// are NO dead Refund/Redesignate/Request-info buttons — this is a de-mock.
export function AttentionPanel({
  attention,
  onCancelRecurring,
  cancelingContributionId,
  sharedLimitWarning,
}: {
  /**
   * The over-limit alarms (one per DONOR past the jurisdiction's limit, GitHub #729 item 2) and
   * the incomplete-donor-info rows. The alarm INFORMS: it states the position and names no remedy
   * (ADR-078 gates the cures in the resolution flows).
   */
  attention: CurrentRead<DashboardAttentionDto>;
  /**
   * #618 — the one-click "cancel this donor's recurring gift". Called with the donor's most recent
   * series-linked contributionId; the server maps it to the series and cancels just that one.
   * Rendered only on an alarm whose donor actually has a series. Omitted ⇒ no cancel button (keeps
   * the panel usable without a mutation wired in, e.g. in isolation tests).
   *
   * This is not a cure and does not contradict "the alarm prescribes nothing": it stops FUTURE
   * money rather than resolving money already received.
   */
  onCancelRecurring?: (contributionId: string) => void;
  /** The contributionId whose cancel is currently in flight — disables that row's button. */
  cancelingContributionId?: string | null;
  /**
   * The standing warning for a joint committee whose candidate also has their own committee: the
   * over-limit rows here cannot see the receipts of that other committee. Null or omitted shows none.
   */
  sharedLimitWarning?: string | null;
}) {
  // The panel's lists exist only once the read answered. A failed or offline read shows its
  // notice; the empty lists below are never rendered as "nothing needs attention".
  const answered = match<CurrentRead<DashboardAttentionDto>, AttentionView>(attention, {
    ready: ({ data }) => ({ state: 'ready' as const, data }),
    failed: () => ({ state: 'error' as const, data: null }),
    offline: () => ({ state: 'error' as const, data: null }),
    loading: () => ({ state: 'loading' as const, data: null }),
    idle: () => ({ state: 'loading' as const, data: null }),
  });
  const loading = answered.state === 'loading';
  const error = answered.state === 'error';
  const overLimit: DonorPositionAlarm[] = answered.data?.overLimit ?? NO_ROWS;
  const incompleteInfo: AttentionContributionDto[] = answered.data?.incompleteInfo ?? NO_ROWS;
  const duplicateFlagCount = answered.data?.duplicateContactFlagCount ?? 0;

  const [tab, setTab] = useState<'all' | 'over' | 'missing'>('all');

  // The two categories no longer share a DTO — over-limit is per donor, incomplete-info is per
  // contribution — so each is normalized to the shape the shared row actually renders. Keeps one
  // grid and one hover/link treatment instead of two near-identical row bodies.
  type Row = {
    key: string;
    issue: 'over_limit' | 'missing_info';
    donorName: string;
    /** Deep-link target in the Close ledger; absent opens the ledger itself. */
    contributionId?: string;
    subtitle: string;
    badge: ReactNode;
    /** Present only when this donor has a recurring series to offer to cancel. */
    cancelContributionId?: string | null;
  };

  const overRows: Row[] = overLimit.map((alarm) => ({
    key: `over_limit-${alarm.contactId}`,
    issue: 'over_limit' as const,
    donorName: alarm.donorName,
    contributionId: alarm.mostRecentContributionId ?? undefined,
    // The deadline rides the subtitle rather than the badge: the badge states the position, this
    // states how long there is to act on it. Omitted entirely where the jurisdiction declares no
    // cure window — a borrowed deadline is worse than none (#744).
    subtitle:
      `${alarm.contributionCount} ${alarm.contributionCount === 1 ? 'gift' : 'gifts'}`
      + ` · latest ${fmtDateShort(alarm.mostRecentGiftDate)}`
      + (alarm.cureDeadline ? ` · cure by ${fmtDateShort(alarm.cureDeadline)}` : ''),
    badge: (
      // States the position and stops. The figures come from the server, which resolved the
      // jurisdiction's own limit — the panel invents no cap and recommends no remedy.
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11.5px] font-semibold bg-rose-100 text-rose-700">
        {alarm.positions
          .map(
            (p: DonorPositionOverage) =>
              `${fmtMoneyShort(p.total)} toward ${p.periodLabel} — ${fmtMoneyShort(p.excess)} over the ${fmtMoneyShort(p.limit)} limit`,
          )
          .join(' · ')}
      </span>
    ),
    cancelContributionId: alarm.recurringContributionId,
  }));

  const missingRows: Row[] = incompleteInfo.map((row) => ({
    key: `missing_info-${row.contributionId}`,
    issue: 'missing_info' as const,
    donorName: row.donorName,
    contributionId: row.contributionId,
    subtitle: `${fmtMoneyShort(row.amount)} on ${fmtDateShort(row.date)}`,
    badge: (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11.5px] font-semibold bg-amber-100 text-amber-700">
        Missing {row.missing.join(' · ')}
      </span>
    ),
  }));

  const allRows = [...overRows, ...missingRows];
  const total = allRows.length;

  const list = tab === 'over' ? overRows : tab === 'missing' ? missingRows : allRows;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex justify-between items-start gap-4 mb-3.5 flex-wrap">
        <div>
          <div className="text-base font-extrabold text-slate-900 tracking-[-.01em] flex items-center gap-2.5">
            Attention required before next filing
            {!loading && !error && (
              <span className="text-xs font-extrabold text-white bg-rose-600 px-2 py-0.5 rounded tracking-[.02em] font-mono">{total}</span>
            )}
          </div>
          {/* Possible-duplicate donor lead: a small persistent count so the
              treasurer works duplicates down early. Display-only for the pilot
              (no duplicate-resolution route yet). Shown only when > 0. */}
          {duplicateFlagCount > 0 && (
            <div
              data-testid="dashboard-duplicate-flags"
              className="text-[12.5px] text-slate-500 mt-1.5"
            >
              {duplicateDonorLeadLabel(duplicateFlagCount)}
            </div>
          )}
          {sharedLimitWarning && (
            <div
              data-testid="dashboard-shared-limit-warning"
              className="mt-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12.5px] text-amber-800"
            >
              {sharedLimitWarning}
            </div>
          )}
        </div>
        {!loading && !error && (
          <div className="inline-flex gap-0.5 bg-slate-100 rounded-md p-[3px]">
            {(
              [
                ['all', `All`, total],
                ['over', `Over limit`, overRows.length],
                ['missing', `Missing info`, missingRows.length],
              ] as const
            ).map(([id, label, count]) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={cn(
                  'px-2.5 py-1 rounded text-xs font-semibold inline-flex items-center gap-1.5',
                  tab === id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
                )}
              >
                {label} <span className={cn('text-[11px] font-bold font-mono', tab === id ? 'text-slate-700' : 'text-slate-400')}>{count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {error ? (
        // Inline "couldn't load" — never a fabricated row or count (ADR-017).
        <div className="border-t border-slate-200 pt-4 text-[13px] text-slate-500">
          We couldn’t load your attention items right now.
        </div>
      ) : loading ? (
        // Skeleton, never fabricated donor rows (ADR-017).
        <div className="border-t border-slate-200 pt-4 space-y-2.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-12 rounded-md bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="border-t border-slate-200 pt-6 pb-4 flex items-center gap-2.5 text-[13.5px] text-slate-600">
          <CheckCircle2 className="h-4.5 w-4.5 text-emerald-600" />
          All clear — nothing to resolve before your next filing.
        </div>
      ) : (
        <div className="border-t border-slate-200">
          {list.map((row) => {
            const { issue } = row;
            // #618 — the one-click "cancel this donor's recurring gift": shown only on an alarm
            // whose donor actually has a series, and only when a handler is wired in. A donor with
            // only one-time gifts never offers it.
            const cancelId = row.cancelContributionId;
            const showCancelRecurring = !!cancelId && !!onCancelRecurring;
            const isCanceling = !!cancelId && cancelingContributionId === cancelId;
            return (
              // A row is a grid whose first two columns are wrapped in a `display: contents`
              // Link (so avatar + donor info stay grid items yet the whole area deep-links to
              // the Close ledger), and whose trailing column holds the optional cancel button +
              // chevron OUTSIDE that link — so clicking the button acts, never navigates.
              <div
                key={row.key}
                className="relative grid grid-cols-[36px_1fr_auto] gap-3.5 items-center py-3.5 px-1 border-b border-slate-100 last:border-b-0 hover:bg-slate-50"
              >
                <Link
                  to="/admin/finance/close"
                  search={{ contributionId: row.contributionId }}
                  title="Open this contribution in the Close ledger"
                  className="contents"
                >
                  <div
                    className="w-9 h-9 rounded-full text-white grid place-items-center text-xs font-bold"
                    style={{ background: issue === 'over_limit' ? '#e11d48' : '#d97706' }}
                  >
                    {row.donorName.split(' ').map((p) => p[0]).slice(0, 2).join('')}
                  </div>
                  <div className="min-w-0 flex flex-col gap-1.5">
                    <div className="flex items-baseline gap-2.5 flex-wrap">
                      <div className="text-sm font-bold text-slate-900">{row.donorName}</div>
                      <div className="text-[11.5px] text-slate-500 font-mono">{row.subtitle}</div>
                    </div>
                    <div className="flex items-center gap-2.5 flex-wrap text-xs">{row.badge}</div>
                  </div>
                </Link>
                <div className="flex items-center gap-2 justify-end">
                  {showCancelRecurring && (
                    <button
                      type="button"
                      onClick={() => onCancelRecurring!(cancelId!)}
                      disabled={isCanceling}
                      title="Cancel this donor's monthly recurring gift"
                      className="inline-flex items-center rounded-md border border-rose-200 bg-white px-2.5 py-1 text-[11.5px] font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isCanceling ? 'Canceling…' : 'Cancel recurring gift'}
                    </button>
                  )}
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Finish-setting-up card ───────────────────────────────────────────────────
// Leads the dashboard while setup is incomplete. "Incomplete" = a bank OR a
// payment processor is still missing (|| — the checklist framing keeps the nudge
// alive until BOTH exist), mirroring the Settings tab's "!" indicator. The gate
// stays hidden while onboarding status is undefined (cold load) so nothing
// flashes on first paint, and disappears once both counts are > 0.
export function shouldShowSetupCard(
  // Only the two counts drive the gate — narrowed so callers (and tests) don't
  // have to synthesize the rest of the onboarding DTO.
  status: Pick<OnboardingStatusDto, 'bankCount' | 'processorCount'> | undefined,
): boolean {
  // Delegates to the single source of truth so the dashboard gate and the
  // Settings-tab "!" indicator can never drift apart.
  return isOnboardingIncomplete(status);
}

function SetupChecklistRow({
  itemKey,
  label,
  done,
}: {
  itemKey: 'bank' | 'processor';
  label: string;
  done: boolean;
}) {
  return (
    <li data-testid={`setup-item-${itemKey}`} className="flex items-center gap-2.5">
      {done ? (
        <span
          data-testid={`setup-item-${itemKey}-done`}
          className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 text-emerald-700"
        >
          <Check className="h-3 w-3" aria-hidden />
          <span className="sr-only">done</span>
        </span>
      ) : (
        <span
          data-testid={`setup-item-${itemKey}-undone`}
          className="inline-flex items-center justify-center h-5 w-5 rounded-full border border-amber-300"
          aria-hidden
        />
      )}
      <span className={cn('text-sm', done ? 'text-amber-800 line-through' : 'text-amber-900 font-medium')}>
        {label}
      </span>
    </li>
  );
}

// Prop-driven presentational card (same idiom as ReportsRow / AccountsPanel) so
// it's testable without the query stack. Amber-tinted rounded card — no left/right
// border accent stripes (banned).
export function SetupCard({
  bankCount,
  processorCount,
}: {
  bankCount: number;
  processorCount: number;
}) {
  return (
    <div
      data-testid="dashboard-setup-card"
      className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-3.5"
    >
      <div className="text-base font-extrabold text-amber-900 tracking-[-.01em]">Finish setting up</div>
      <p className="text-[13px] text-amber-800 mt-1">
        Connect your bank and payment processor so imports and fee tracking work.
      </p>
      <ul className="mt-3.5 space-y-2">
        <SetupChecklistRow itemKey="bank" label="Add a bank account" done={bankCount > 0} />
        <SetupChecklistRow itemKey="processor" label="Add a payment processor" done={processorCount > 0} />
      </ul>
      <Link
        to="/admin/finance/settings/setup"
        className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 transition-colors"
      >
        Go to Banks &amp; processors <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </Link>
    </div>
  );
}

// ── Accept-online-donations promo ────────────────────────────────────────────
// A calm opportunity nudge — the dashboard's only surface (besides the Donations
// nav tab) that points at Stripe Connect onboarding. Shown while donations are NOT
// yet live so a committee discovers they can accept online gifts; it disappears
// once the connected account can actually take charges.
//
// "Live" mirrors PaymentsOnboardingPage's `onboarded` gate exactly (charges enabled
// AND account details submitted) so the card and that page never disagree about
// whether setup is finished. Undefined status (cold load / disabled query) keeps the
// card hidden so nothing flashes on first paint — the same restraint as
// shouldShowSetupCard. Deliberately styled emerald/opportunity, NOT the amber
// "you must fix this" of SetupCard, and placed lower on the page so the two never
// read as stacked alarms.
export function shouldShowDonationsCard(
  // Only the two "is it live?" flags drive the gate — narrowed so callers (and
  // tests) don't have to synthesize the rest of the payments-status DTO.
  status: Pick<PaymentsStatusDto, 'chargesEnabled' | 'detailsSubmitted'> | undefined,
): boolean {
  if (!status) return false;
  return !(status.chargesEnabled && status.detailsSubmitted);
}

// Prop-driven presentational card (same idiom as SetupCard) so it's testable
// without the query stack. No left/right border accent stripes (banned).
export function DonationsPromoCard() {
  return (
    <div
      data-testid="dashboard-donations-card"
      className="bg-white border border-slate-200 rounded-2xl p-5 mb-3.5 shadow-sm flex items-start gap-4"
    >
      <div className="shrink-0 h-10 w-10 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
        <HandCoins className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-base font-extrabold text-slate-900 tracking-[-.01em]">
          Accept online donations
        </div>
        <p className="text-[13px] text-slate-600 mt-1 max-w-xl">
          Give supporters a secure page to give online. Funds land in your
          committee&apos;s account and every gift flows straight into your finance
          records — no manual entry.
        </p>
        <Link
          to="/admin/finance/payments"
          className="mt-3.5 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
        >
          Set up donations <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
export function FinanceDashboard() {
  const [period, setPeriod] = useState<PeriodId>('current_period');
  const navigate = useNavigate();

  const health = useFinanceHealth();
  const { data: onboardingStatus } = useOnboardingStatus();
  // Drives the "Accept online donations" promo — shown until the connected
  // account can actually take charges (see shouldShowDonationsCard).
  const { data: paymentsStatus } = usePaymentsStatus();
  const { data: deadlines } = useFinanceDeadlines({ status: DeadlineStatus.Upcoming, pageSize: 5 });
  const { data: reports } = useFinanceReports({ status: ReportStatus.Filed, pageSize: 5 });
  const { data: settings } = useFinanceSettings();
  const { data: complianceProfile } = useComplianceProfile();
  const { downloadPackage } = useDownloadReportPackage();
  // Touch metrics so the cache is warmed; not all fields are wired yet.
  useFinanceMetrics();

  // Download the filed report's submission package (ZIP), mirroring
  // ReportDetailPage. Failures surface to the console; the dashboard card has
  // no inline error slot, and a download is a best-effort convenience here.
  const handleDownloadPackage = async (reportId: string) => {
    try {
      const result = await downloadPackage(reportId);
      triggerBrowserDownload(result.zipBlob, result.fileName);
    } catch (e) {
      console.error('Failed to download report package', e);
    }
  };

  // The next chronological deadline. Health names it first; the deadline list stands in
  // while health is unanswered, for the window and the countdown only. Whether the
  // treasurer is caught up is health's answer alone (see the ReportsRow match below).
  const healthData = health.status === 'ready' ? health.data : null;
  const nextDeadline = healthData?.nextDeadline ?? deadlines?.items?.[0] ?? null;
  const lastFiledReport = reports?.items?.[0] ?? null;
  const rulesetCode = settings?.rulesetCode ?? null;
  // Filing-model boundary (§29/§30/§32): resolved once here from the campaign's
  // registered builder workflow, then threaded into the banner + report cards so
  // the "you upload / nothing files automatically" copy only shows for the
  // append-only portal-upload model (PortalOnly), never for federal e-file.
  const portalModel = usePortalUploadModel();

  // Resolve the selected period to a concrete [from, to] window (ADR-017).
  // Campaign-today comes from the campaign timezone, never new Date(). The
  // custom range defaults to the current filing period (or YTD).
  const campaignToday = getCampaignTodayString();
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  const windowFor = (p: PeriodId, deadline: FilingDeadlineDto | null, customFrom: string, customTo: string) =>
    computePeriodWindow(p, {
      campaignToday,
      periodStart: deadline?.periodStart ?? null,
      periodEnd: deadline?.periodEnd ?? null,
      formationDate: settings?.committeeFormationDate ?? null,
      customFrom,
      customTo,
    });
  const currentWindow = windowFor(period === 'custom' ? 'current_period' : period, nextDeadline, '', '');
  const customFrom = customRange?.from ?? currentWindow.from;
  const customTo = customRange?.to ?? currentWindow.to;
  // ADR-017, "no silent surprise": "This filing period" is the window health names, so
  // the summary waits on health and fails with it. Without that, an unanswered health
  // falls back to year-to-date totals under the "This filing period" label.
  const summaryWindow: Read<{ from: string; to: string }> =
    period === 'current_period'
      ? mapRead(health, (h) => windowFor(period, h.nextDeadline ?? deadlines?.items?.[0] ?? null, customFrom, customTo))
      : { status: 'ready', data: windowFor(period, nextDeadline, customFrom, customTo), refreshing: false };
  const summary = useDashboardSummary(whenReady(summaryWindow));
  const lastFiledCash = useLastFiledCash();
  const roster = useDashboardRoster();
  const attention = useDashboardAttention();

  // Sync now (dashboard): the free `/transactions/sync` cached pull, so a treasurer about
  // to file can grab the latest their bank has already shared without waiting for the daily
  // poll — NOT a billable live refresh. The connection list is Finance:Manage, so gate the
  // fetch on that tier (a viewer skips the doomed 403). The roster rows carry no connection
  // id, so this syncs every live (Active) connection at once. The per-connection server gate
  // 409s any stacked sync; here one in-flight run just disables the button.
  const canManageFinance = useCampaignStore((s) => s.canManage('Finance'));
  const { data: bankConnections } = useBankConnections({ enabled: canManageFinance });
  const activeConnections = (bankConnections ?? []).filter(
    (c) => c.status === BankConnectionStatus.Active,
  );
  const { mutateAsync: syncConnection } = useSyncBankConnection();
  const [syncingBanks, setSyncingBanks] = useState(false);
  const handleSyncBanks = async () => {
    if (activeConnections.length === 0) return;
    setSyncingBanks(true);
    try {
      await Promise.all(activeConnections.map((c) => syncConnection(c.id)));
    } catch {
      // A failed / already-in-progress sync leaves connections untouched — the daily poll
      // keeps the ledger current regardless, and the treasurer can retry.
    } finally {
      setSyncingBanks(false);
    }
  };
  // #618 — the over-limit flag's one-click "cancel this donor's recurring gift".
  const { mutate: cancelOverLimitRecurring, isPending: cancelingRecurring, variables: cancelingContributionId } =
    useCancelOverLimitRecurring();

  // The "Next report due" card's Staged/Flagged stats come from the server-computed
  // `health.owedReportReadiness` counts (ADR-026 PR-A), which the API computes over
  // `owedWindow ?? calendarOpenPeriod` by reusing the sort-inbox projection seam — so
  // they equal the period-scoped close-page pips with zero drift, and the dashboard
  // no longer downloads the entire all-time ledger just to derive four numbers (it
  // renders no ledger rows). While health loads (undefined) or when no period is open
  // (null), `?.ready` / `?.notReady` are undefined → the card shows its muted "—",
  // never a fabricated 0 (ADR-017). Caught-up window change (intended, ADR-026): when
  // caught up, these count over the calendar-open period, not `nextDeadline`.
  const readiness = healthData?.owedReportReadiness ?? null;
  const reportToActOn = healthData?.reportToActOn ?? null;

  const reportsRow = (h: FinanceHealthDto | null) => (
        <ReportsRow
          // Lead with the owed report (reportToActOn) when present; else the next
          // chronological deadline. When the owed report is EARLIER than the
          // countdown (reportToActOnIsNextDeadline === false), the next deadline
          // rides along as secondary context. Caught up ⇒ the "all caught up" state
          // over the next upcoming deadline.
          next={h?.reportToActOn ?? nextDeadline}
          secondaryDeadline={h?.reportToActOn && !h.reportToActOnIsNextDeadline ? nextDeadline : null}
          caughtUp={h !== null && !h.reportToActOn && !!nextDeadline}
          last={lastFiledReport}
          amendmentPending={h !== null && h.reportsNeedingAmendment.length > 0}
          rulesetCode={rulesetCode}
          portalUpload={portalModel}
          // Sourced from health.owedReportReadiness (ADR-026 PR-A) — the server's
          // close-ledger pips over the owed window, NOT the campaign-wide health
          // counts — so the card EQUALS the ledger footer: Staged = "ready" (Done,
          // reporting), Flagged = "not ready" (NeedsAction). Undefined while health
          // loads (or when no period is open) → muted "—", never a fabricated 0.
          stagedCount={h?.owedReportReadiness?.ready}
          flaggedCount={h?.owedReportReadiness?.notReady}
          // Replaces the "Next report due" card until the treasurer resolves it.
          pendingExportReport={h?.pendingExportReport ?? null}
          onJumpToFilings={() => navigate({ to: '/admin/finance/filings' })}
          onViewReport={(reportId) =>
            navigate({ to: '/admin/finance/reports/$reportId', params: { reportId } })
          }
          onDownloadPackage={handleDownloadPackage}
          onGoConfirmExport={(reportId) =>
            navigate({ to: '/admin/finance/reports/$reportId', params: { reportId } })
          }
        />
  );

  return (
    <div className="px-1 pb-15">
      <div className="flex justify-between items-end mb-4 gap-5">
        <div>
          <div className="text-[11px] font-bold text-blue-600 tracking-[.1em] font-mono">DASHBOARD</div>
          <div className="text-[28px] font-extrabold text-slate-900 tracking-[-.02em] mt-1">Finance &amp; compliance</div>
        </div>
        <div className="text-[11.5px] text-slate-500 font-medium font-mono">
          As of{' '}
          {new Date().toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </div>
      </div>

      {shouldShowSetupCard(onboardingStatus) && (
        <SetupCard
          bankCount={onboardingStatus!.bankCount}
          processorCount={onboardingStatus!.processorCount}
        />
      )}

      <DashHero
        period={period}
        setPeriod={setPeriod}
        summary={summary}
        lastFiledCash={lastFiledCash}
        customFrom={customFrom}
        customTo={customTo}
        onCustomRange={(f, t) => setCustomRange({ from: f, to: t })}
        // §15 — the same staged (ready) pip the "Next report due" card reads
        // (health.owedReportReadiness, ADR-026 PR-A), so an empty current period
        // points at the OWED report (reportToActOn) — the earlier report those
        // staged items actually belong to — not the empty next period (the prior lie).
        stagedCount={readiness?.ready}
        owedReportName={reportToActOn?.name ?? null}
      />
      {/* Reports awaiting an amendment (see NeedsAmendmentAlerts) — the state the
          `status === Filed` surfaces below cannot show. Self-hides when there are none. */}
      <NeedsAmendmentAlerts />
      {/* The filing-status card is health's answer. A failed health read says so; it never
          becomes "all caught up". While health loads, the card keeps its muted "—" counts. */}
      {match(health, {
        failed: () => <HealthReadNotice>We couldn’t load your filing status. Refresh to retry.</HealthReadNotice>,
        offline: () => (
          <HealthReadNotice>You’re offline. Your filing status will load when you reconnect.</HealthReadNotice>
        ),
        idle: () => reportsRow(null),
        loading: () => reportsRow(null),
        ready: ({ data }) => reportsRow(data),
      })}
      {shouldShowDonationsCard(paymentsStatus) && <DonationsPromoCard />}

      <AccountsPanel
        roster={roster}
        campaignToday={campaignToday}
        onJumpToSources={() => navigate({ to: '/admin/finance/sources' })}
        canSync={activeConnections.length > 0}
        onSyncBanks={handleSyncBanks}
        syncing={syncingBanks}
      />
      <AttentionPanel
        attention={attention}
        onCancelRecurring={(contributionId) => cancelOverLimitRecurring(contributionId)}
        cancelingContributionId={cancelingRecurring ? cancelingContributionId ?? null : null}
        sharedLimitWarning={
          complianceProfile?.jointCommitteeSharesALimit ? complianceProfile.jointCommitteeSharedLimitWarning : null
        }
      />

      {/* Hidden link to keep tanstack router type-aware of these routes if not yet defined */}
      <div className="hidden">
        <Link to="/admin/finance/filings">Filings</Link>
        <Link to="/admin/finance/sources">Sources</Link>
      </div>
    </div>
  );
}

function HealthReadNotice({ children }: { children: ReactNode }) {
  return (
    <div
      data-testid="dashboard-health-unavailable"
      className="bg-white border border-slate-200 rounded-2xl p-5 mb-3.5 shadow-sm text-[13px] text-slate-500"
    >
      {children}
    </div>
  );
}
